import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Hash, UserPlus } from 'lucide-react';
import { api, type HouseSettings, type MeResponse, type RepAccount, type Salesperson } from '@/lib/api';
import { cn } from '@/lib/utils';

const input =
  'border-input bg-card focus:ring-ring h-11 w-full rounded-lg border px-3 text-base focus:ring-2 focus:outline-none';

/**
 * Admin : comptes reps (création, n° vendeur ERP, désactivation, rôle, mot de
 * passe) et visibilité des comptes maison.
 */
export default function Accounts({ session }: { session: MeResponse }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['reps'],
    queryFn: () => api<{ reps: RepAccount[] }>('/api/admin/reps'),
  });
  const salespersons = useQuery({
    queryKey: ['salespersons'],
    queryFn: () => api<{ salespersons: Salesperson[] }>('/api/admin/salespersons'),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['reps'] });
  const known = salespersons.data?.salespersons ?? [];
  const all = list.data?.reps ?? [];
  const inactive = all.filter((r) => !r.isActive);
  const row = (r: RepAccount) => (
    <RepRow key={r.id} rep={r} salespersons={known} isMe={r.id === session.user.id} onSaved={refresh} />
  );

  return (
    <>
      <h1 className="page-title">Comptes</h1>
      <CreateForm salespersons={known} onCreated={refresh} />
      <section className="panel p-4">
        {list.isError && <p className="text-destructive text-sm">{(list.error as Error).message}</p>}
        <ul className="divide-border divide-y">{all.filter((r) => r.isActive).map(row)}</ul>
        {inactive.length > 0 && (
          <details className="border-border mt-2 border-t pt-3">
            <summary className="text-muted-foreground cursor-pointer text-sm font-medium select-none">
              Comptes désactivés ({inactive.length})
            </summary>
            <ul className="divide-border divide-y">{inactive.map(row)}</ul>
          </details>
        )}
      </section>
      <HouseAccounts salespersons={known} />
    </>
  );
}

/** « 24 · MARCO B » */
const labelOf = (number: string, salespersons: Salesperson[]) => {
  const name = salespersons.find((s) => s.number === number)?.name;
  return name ? `${number} · ${name}` : number;
};

/** Choix de plusieurs n° vendeur parmi ceux du carnet d'adresses. */
function NumberPicker({
  salespersons,
  value,
  onChange,
}: {
  salespersons: Salesperson[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (n: string) => onChange(value.includes(n) ? value.filter((v) => v !== n) : [...value, n]);
  return (
    <div className="flex flex-wrap gap-2">
      {salespersons.map((s) => {
        const on = value.includes(s.number);
        return (
          <button
            key={s.number}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(s.number)}
            className={cn(
              'h-9 rounded-full border px-3 text-sm',
              on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-accent',
            )}
          >
            {s.number} · {s.name ?? '—'} <span className="opacity-70">({s.customers})</span>
          </button>
        );
      })}
    </div>
  );
}

function CreateForm({ salespersons, onCreated }: { salespersons: Salesperson[]; onCreated: () => void }) {
  const empty = {
    username: '',
    fullName: '',
    email: '',
    password: '',
    role: 'REP' as 'REP' | 'ADMIN',
    salespersonNumbers: [] as string[],
  };
  const [f, setF] = useState(empty);
  const create = useMutation({
    mutationFn: () =>
      api('/api/admin/reps', {
        method: 'POST',
        body: JSON.stringify({ ...f, email: f.email.trim() || null, password: f.password || undefined }),
      }),
    onSuccess: () => {
      setF(empty);
      onCreated();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="panel space-y-3 p-4">
      <h2 className="panel-title flex items-center gap-2">
        <UserPlus className="size-5" /> Nouveau compte
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-sm font-medium">Nom complet</span>
          <input value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} required className={input} />
        </label>
        <label className="space-y-1">
          <span className="text-sm font-medium">Nom d'utilisateur</span>
          <input
            value={f.username}
            onChange={(e) => setF({ ...f, username: e.target.value })}
            autoCapitalize="none"
            required
            className={input}
          />
        </label>
        <label className="space-y-1">
          <span className="text-sm font-medium">Courriel (facultatif)</span>
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} />
        </label>
        <label className="space-y-1">
          <span className="text-sm font-medium">Mot de passe initial (facultatif, 8 min.)</span>
          <input
            value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })}
            minLength={8}
            placeholder="Vide : à définir plus tard"
            autoComplete="new-password"
            className={input}
          />
        </label>
        <label className="space-y-1">
          <span className="text-sm font-medium">Rôle</span>
          <select
            value={f.role}
            onChange={(e) => setF({ ...f, role: e.target.value as 'REP' | 'ADMIN' })}
            className={input}
          >
            <option value="REP">Représentant</option>
            <option value="ADMIN">Administrateur</option>
          </select>
        </label>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium">N° vendeur ERP (ses clients)</span>
        <NumberPicker
          salespersons={salespersons}
          value={f.salespersonNumbers}
          onChange={(salespersonNumbers) => setF({ ...f, salespersonNumbers })}
        />
      </div>
      {create.isError && <p className="text-destructive text-sm">{(create.error as Error).message}</p>}
      <button
        type="submit"
        disabled={create.isPending}
        className="bg-primary text-primary-foreground hover:bg-primary-dark btn-caps h-11 rounded-lg px-5 transition-colors disabled:opacity-50"
      >
        Créer le compte
      </button>
    </form>
  );
}

function RepRow({
  rep,
  salespersons,
  isMe,
  onSaved,
}: {
  rep: RepAccount;
  salespersons: Salesperson[];
  isMe: boolean;
  onSaved: () => void;
}) {
  const [resetting, setResetting] = useState(false);
  const [pwd, setPwd] = useState('');
  const [numbers, setNumbers] = useState<string[] | null>(null);
  const [editing, setEditing] = useState<{ fullName: string; email: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const save = useMutation({
    mutationFn: (body: object) =>
      api(`/api/admin/reps/${rep.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      setResetting(false);
      setPwd('');
      setNumbers(null);
      setEditing(null);
      onSaved();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/admin/reps/${rep.id}`, { method: 'DELETE' }),
    onSuccess: onSaved,
    // Refus du serveur (une commande vient d'être créée) : la liste se remet à jour.
    onError: () => {
      setConfirmDelete(false);
      onSaved();
    },
  });
  const canDelete = !isMe && rep.quoteCount === 0;
  const btn = 'bg-card hover:bg-accent h-9 rounded-lg border px-3 text-sm transition-colors';

  return (
    <li className={cn('space-y-2 py-3', !rep.isActive && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold">{rep.fullName}</span>
        <span className="text-muted-foreground font-mono text-sm">{rep.username}</span>
        <span className="bg-muted rounded-full px-2 py-0.5 text-xs">
          {rep.role === 'ADMIN' ? 'Administrateur' : 'Représentant'}
        </span>
        {!rep.isActive && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-900">Désactivé</span>}
        {!rep.hasPassword && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">Sans mot de passe</span>
        )}
        {rep.quoteCount > 0 && (
          <span className="bg-muted rounded-full px-2 py-0.5 text-xs">
            {rep.quoteCount} commande{rep.quoteCount > 1 ? 's' : ''}
          </span>
        )}
        <span className="text-muted-foreground text-xs">
          Dernière connexion : {rep.lastLoginAt ? new Date(rep.lastLoginAt).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }) : 'jamais'}
        </span>
      </div>
      {rep.email && <div className="text-muted-foreground text-sm">{rep.email}</div>}
      <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
        <Hash className="size-4" />
        {rep.salespersonNumbers.length
          ? rep.salespersonNumbers.map((n) => (
              <span key={n} className="bg-muted text-foreground rounded-full px-2 py-0.5 text-xs">
                {labelOf(n, salespersons)}
              </span>
            ))
          : 'Aucun n° vendeur'}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setEditing((e) => (e ? null : { fullName: rep.fullName, email: rep.email ?? '' }))}
          className={btn}
        >
          Modifier
        </button>
        {!isMe && (
          <>
            <button onClick={() => save.mutate({ isActive: !rep.isActive })} className={btn}>
              {rep.isActive ? 'Désactiver' : 'Réactiver'}
            </button>
            <button onClick={() => save.mutate({ role: rep.role === 'ADMIN' ? 'REP' : 'ADMIN' })} className={btn}>
              {rep.role === 'ADMIN' ? 'Rendre représentant' : 'Rendre administrateur'}
            </button>
          </>
        )}
        <button onClick={() => setNumbers((n) => (n ? null : rep.salespersonNumbers))} className={btn}>
          N° vendeur
        </button>
        <button onClick={() => setResetting((r) => !r)} className={btn}>
          {rep.hasPassword ? 'Réinitialiser le mot de passe' : 'Définir le mot de passe'}
        </button>
        {canDelete && (
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-destructive h-9 rounded-lg border border-red-200 bg-card px-3 text-sm transition-colors hover:bg-red-50"
          >
            Supprimer
          </button>
        )}
        {!isMe && rep.quoteCount > 0 && (
          <span className="text-muted-foreground text-xs">
            A des commandes : se désactive, ne se supprime pas (historique conservé).
          </span>
        )}
      </div>
      {confirmDelete && (
        <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-950">
          <p>
            Supprimer définitivement le compte de <strong>{rep.fullName}</strong> ? Ses clients et son n° vendeur
            dans l'ERP ne changent pas.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="h-10 rounded-lg bg-red-700 px-4 font-semibold text-white transition-colors hover:bg-red-800 disabled:opacity-50"
            >
              Confirmer la suppression
            </button>
            <button onClick={() => setConfirmDelete(false)} className={cn(btn, 'h-10')}>
              Annuler
            </button>
          </div>
        </div>
      )}
      {remove.isError && <p className="text-destructive text-sm">{(remove.error as Error).message}</p>}
      {editing && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ fullName: editing.fullName, email: editing.email.trim() || null });
          }}
          className="bg-muted grid gap-3 rounded-xl p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        >
          <label className="space-y-1">
            <span className="text-sm font-medium">Nom complet</span>
            <input
              value={editing.fullName}
              onChange={(e) => setEditing({ ...editing, fullName: e.target.value })}
              required
              className={input}
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium">Courriel (facultatif)</span>
            <input
              type="email"
              value={editing.email}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              className={input}
            />
          </label>
          <button
            type="submit"
            disabled={save.isPending || !editing.fullName.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary-dark h-11 rounded-lg px-4 font-semibold transition-colors disabled:opacity-50"
          >
            Enregistrer
          </button>
        </form>
      )}
      {numbers && (
        <div className="bg-muted space-y-3 rounded-xl p-3">
          <NumberPicker salespersons={salespersons} value={numbers} onChange={setNumbers} />
          <button
            onClick={() => save.mutate({ salespersonNumbers: numbers })}
            disabled={save.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary-dark h-10 rounded-lg px-4 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            Enregistrer les n° vendeur
          </button>
        </div>
      )}
      {resetting && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Nouveau mot de passe (8 min.)"
            autoComplete="new-password"
            className={cn(input, 'w-64')}
          />
          <button
            onClick={() => save.mutate({ password: pwd })}
            disabled={pwd.length < 8 || save.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary-dark h-11 rounded-lg px-4 font-semibold transition-colors disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      )}
      {save.isError && <p className="text-destructive text-sm">{(save.error as Error).message}</p>}
    </li>
  );
}

/** Comptes maison (98 GARAGE QUEBEC, 99 COMPTE MAISON…) : visibles des reps ou non. */
function HouseAccounts({ salespersons }: { salespersons: Salesperson[] }) {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['house-settings'],
    queryFn: () => api<HouseSettings>('/api/admin/settings/house'),
  });
  const save = useMutation({
    mutationFn: (s: HouseSettings) =>
      api<HouseSettings>('/api/admin/settings/house', { method: 'PUT', body: JSON.stringify(s) }),
    onSuccess: (s) => qc.setQueryData(['house-settings'], s),
  });
  const s = settings.data;

  return (
    <section className="panel space-y-3 p-4">
      <h2 className="panel-title flex items-center gap-2">
        <Building2 className="size-5" /> Comptes maison
      </h2>
      <p className="text-muted-foreground text-sm">
        Clients rattachés à un n° vendeur qui n'est pas un représentant sur la route (et clients sans vendeur).
      </p>
      {s && (
        <>
          <div className="bg-muted inline-flex rounded-lg p-1" role="radiogroup" aria-label="Visibilité des comptes maison">
            {[
              { value: false, label: 'Admins seulement' },
              { value: true, label: 'Tous les reps' },
            ].map((o) => (
              <button
                key={o.label}
                role="radio"
                aria-checked={s.houseVisibleToReps === o.value}
                onClick={() => save.mutate({ ...s, houseVisibleToReps: o.value })}
                className={cn(
                  'h-10 rounded-md px-4 text-sm font-medium',
                  s.houseVisibleToReps === o.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium">N° vendeur « maison »</span>
            <NumberPicker
              salespersons={salespersons}
              value={s.houseNumbers}
              onChange={(houseNumbers) => save.mutate({ ...s, houseNumbers })}
            />
          </div>
        </>
      )}
      {save.isError && <p className="text-destructive text-sm">{(save.error as Error).message}</p>}
    </section>
  );
}
